import { useEffect, useState, useCallback } from "react";
import {
  getFormConfig,
  updateFormConfigKey,
  getAvailability,
  addAvailabilityBlock,
  deleteAvailabilityBlock,
  getDateOverrides,
  addDateOverride,
  deleteDateOverride,
} from "../eventsApi";
import type { AvailabilityBlock, DateOverride } from "../types";

type TimeOption = { value: string; label: string };
type DurationPricing = { hours: number; label: string; price_per_person: number };
type FoodPackagePrice = { name: string; price_per_person: number };
type CannedMessage = { label: string; body: string };

const ALL_TIME_OPTIONS: TimeOption[] = [
  { value: "morning", label: "Morning (10am–12pm)" },
  { value: "afternoon", label: "Afternoon (12pm–5pm)" },
  { value: "evening", label: "Evening (5pm–10pm)" },
];

const DEFAULT_DURATION_PRICING: DurationPricing[] = [
  { hours: 2, label: "2 Hours", price_per_person: 35 },
  { hours: 3, label: "3 Hours", price_per_person: 45 },
  { hours: 4, label: "4 Hours", price_per_person: 55 },
];

const DEFAULT_FOOD_PACKAGES: FoodPackagePrice[] = [
  { name: "Food Package A", price_per_person: 0 },
  { name: "Food Package B", price_per_person: 0 },
  { name: "Food Package C", price_per_person: 0 },
];

export function SettingsView() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [toast, setToast] = useState("");

  // Form config state
  const [minGroupSize, setMinGroupSize] = useState(20);
  const [failureMessage, setFailureMessage] = useState(
    "Group events require a minimum of 20 guests. You can make a reservation for less than 20 people here: https://tribalaxe.com/reserve/"
  );
  const [enabledTimes, setEnabledTimes] = useState<string[]>(["afternoon", "evening"]);
  const [confirmationMessage, setConfirmationMessage] = useState(
    "We've received your group event inquiry. Our events team will review your request and be in touch within 24 hours."
  );
  const [depositPct, setDepositPct] = useState(50);
  const [durationPricing, setDurationPricing] = useState<DurationPricing[]>(DEFAULT_DURATION_PRICING);
  const [foodPackagePricing, setFoodPackagePricing] = useState<FoodPackagePrice[]>(DEFAULT_FOOD_PACKAGES);
  const [quoteEmailDetails, setQuoteEmailDetails] = useState("");
  const [cannedMessages, setCannedMessages] = useState<CannedMessage[]>([]);
  const [editingCanned, setEditingCanned] = useState<{ index: number; label: string; body: string } | null>(null);

  // Scheduling state
  const [availBlocks, setAvailBlocks] = useState<AvailabilityBlock[]>([]);
  const [dateOverrides, setDateOverrides] = useState<DateOverride[]>([]);
  const [addingBlock, setAddingBlock] = useState<{ day: number; start: string; end: string } | null>(null);
  const [addingOverride, setAddingOverride] = useState<{ date: string; type: "unavailable" | "available"; start: string; end: string; note: string } | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      const config = await getFormConfig();
      if (config.min_group_size != null) setMinGroupSize(config.min_group_size as number);
      if (config.group_size_failure_message) setFailureMessage(config.group_size_failure_message as string);
      if (config.time_of_day_options) {
        const opts = config.time_of_day_options as TimeOption[];
        setEnabledTimes(opts.map((o) => o.value));
      }
      if (config.confirmation_message) setConfirmationMessage(config.confirmation_message as string);
      if (config.deposit_pct != null) setDepositPct(config.deposit_pct as number);
      if (config.duration_pricing) setDurationPricing(config.duration_pricing as DurationPricing[]);
      if (config.food_package_pricing) setFoodPackagePricing(config.food_package_pricing as FoodPackagePrice[]);
      if (config.quote_email_details != null) setQuoteEmailDetails(config.quote_email_details as string);
      if (Array.isArray(config.canned_messages)) setCannedMessages(config.canned_messages as CannedMessage[]);
    } catch (err) {
      console.error("Failed to load form config", err);
    }

    try {
      const [availRes, overridesRes] = await Promise.all([
        getAvailability(),
        getDateOverrides(),
      ]);
      setAvailBlocks(availRes.data);
      setDateOverrides(overridesRes.data);
    } catch (err) {
      console.error("Failed to load scheduling data", err);
    }

    setLoading(false);
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const save = async (key: string, value: unknown) => {
    setSaving(key);
    try {
      await updateFormConfigKey(key, value);
      showToast("Saved");
    } catch (err) {
      console.error("Save failed", err);
      showToast("Save failed");
    } finally {
      setSaving(null);
    }
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const runScheduling = async (action: () => Promise<void>, successMsg: string) => {
    try {
      await action();
      showToast(successMsg);
    } catch (err) {
      console.error("Scheduling update failed", err);
      showToast("Save failed");
    }
  };

  const handleTimeToggle = (value: string) => {
    setEnabledTimes((prev) => {
      const next = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      if (next.length === 0) return prev;
      const options = ALL_TIME_OPTIONS.filter((t) => next.includes(t.value));
      save("time_of_day_options", options);
      return next;
    });
  };

  const formatTimeDisplay = (time: string): string => {
    const [h, m] = time.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
        Loading settings…
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <h2 className="text-lg font-semibold text-white mb-4">Event Settings</h2>

      {toast && (
        <div className="fixed top-4 right-4 z-50 rounded-md bg-green-600/90 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <div className="space-y-6 max-w-2xl">
        {/* ── Conversation Flow ─────────────────────────────── */}
        <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="text-sm font-semibold text-white mb-1">Conversation Flow</h3>
          <p className="text-xs text-tribal-muted mb-4">
            Configure how the public inquiry form behaves. Changes apply immediately to the widget.
          </p>

          {/* Step 2: Min group size + failure message */}
          <div className="mb-5 border-b border-tribal-border pb-5">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-tribal-muted mb-2">
              Step 2 — Group Size
            </h4>
            <div className="flex items-center gap-3 mb-3">
              <label className="text-sm text-tribal-muted whitespace-nowrap">Minimum guests:</label>
              <input
                type="number"
                min={1}
                value={minGroupSize}
                onChange={(e) => setMinGroupSize(Number(e.target.value))}
                className="w-20 rounded-md border border-tribal-border bg-tribal-bg px-3 py-1.5 text-sm text-white focus:border-tribal-red focus:outline-none"
              />
              <button
                type="button"
                onClick={() => save("min_group_size", minGroupSize)}
                disabled={saving === "min_group_size"}
                className="rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30 disabled:opacity-50"
              >
                {saving === "min_group_size" ? "…" : "Save"}
              </button>
            </div>
            <label className="block text-sm text-tribal-muted mb-1">Failure message (shown when under minimum):</label>
            <textarea
              value={failureMessage}
              onChange={(e) => setFailureMessage(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white resize-none focus:border-tribal-red focus:outline-none"
            />
            <p className="mt-1 text-xs text-tribal-muted">URLs are auto-linked in the widget.</p>
            <button
              type="button"
              onClick={() => save("group_size_failure_message", failureMessage)}
              disabled={saving === "group_size_failure_message"}
              className="mt-2 rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30 disabled:opacity-50"
            >
              {saving === "group_size_failure_message" ? "…" : "Save"}
            </button>
          </div>

          {/* Step 3: Duration Pricing */}
          <div className="mb-5 border-b border-tribal-border pb-5">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-tribal-muted mb-2">
              Step 3 — Duration Pricing
            </h4>
            <p className="text-xs text-tribal-muted mb-3">Set per-person pricing shown on each duration option.</p>
            <div className="space-y-2">
              {durationPricing.map((dp, i) => (
                <div key={dp.hours} className="flex items-center gap-3">
                  <span className="text-sm text-white w-20">{dp.label}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-tribal-muted">$</span>
                    <input
                      type="number"
                      min={0}
                      value={dp.price_per_person}
                      onChange={(e) => {
                        setDurationPricing((prev) =>
                          prev.map((item, idx) =>
                            idx === i ? { ...item, price_per_person: Number(e.target.value) } : item
                          )
                        );
                      }}
                      className="w-20 rounded-md border border-tribal-border bg-tribal-bg px-3 py-1.5 text-sm text-white focus:border-tribal-red focus:outline-none"
                    />
                    <span className="text-sm text-tribal-muted">/person</span>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => save("duration_pricing", durationPricing)}
              disabled={saving === "duration_pricing"}
              className="mt-3 rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30 disabled:opacity-50"
            >
              {saving === "duration_pricing" ? "…" : "Save Pricing"}
            </button>
          </div>

          {/* Step 5: Time of day options */}
          <div className="mb-5 border-b border-tribal-border pb-5">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-tribal-muted mb-2">
              Step 5 — Time of Day
            </h4>
            <p className="text-xs text-tribal-muted mb-2">Toggle which time-of-day options appear:</p>
            <div className="flex gap-3">
              {ALL_TIME_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabledTimes.includes(opt.value)}
                    onChange={() => handleTimeToggle(opt.value)}
                    className="h-4 w-4 rounded border-tribal-border bg-tribal-bg text-tribal-red accent-tribal-red"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Step 10: Confirmation message */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-tribal-muted mb-2">
              Step 10 — Confirmation Message
            </h4>
            <textarea
              value={confirmationMessage}
              onChange={(e) => setConfirmationMessage(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white resize-none focus:border-tribal-red focus:outline-none"
            />
            <p className="mt-1 text-xs text-tribal-muted">
              Available variables: {"{{first_name}}"}, {"{{email}}"}
            </p>
            <button
              type="button"
              onClick={() => save("confirmation_message", confirmationMessage)}
              disabled={saving === "confirmation_message"}
              className="mt-2 rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30 disabled:opacity-50"
            >
              {saving === "confirmation_message" ? "…" : "Save"}
            </button>
          </div>
        </section>

        {/* ── Reminder Defaults ────────────────────────────── */}
        <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="text-sm font-semibold text-white mb-2">Reminder Defaults</h3>
          <p className="text-sm text-tribal-muted">
            Configure default reminder timing for new inquiries. Each reminder can be
            individually adjusted per inquiry.
          </p>
          <div className="mt-3 space-y-2 text-sm text-tribal-muted">
            <div className="flex justify-between">
              <span>Deposit reminder</span>
              <span className="text-white">48 hours after quote</span>
            </div>
            <div className="flex justify-between">
              <span>Food selection</span>
              <span className="text-white">7 days before event</span>
            </div>
            <div className="flex justify-between">
              <span>Final headcount</span>
              <span className="text-white">5 days before event</span>
            </div>
            <div className="flex justify-between">
              <span>Waiver form</span>
              <span className="text-white">3 days before event</span>
            </div>
            <div className="flex justify-between">
              <span>Event day</span>
              <span className="text-white">9:00 AM day-of</span>
            </div>
          </div>
        </section>

        {/* ── Deposit Default ──────────────────────────────── */}
        <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="text-sm font-semibold text-white mb-2">Deposit Default</h3>
          <p className="text-sm text-tribal-muted">
            Default deposit percentage used in the quote builder.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <input
              type="number"
              value={depositPct}
              onChange={(e) => setDepositPct(Number(e.target.value))}
              min={0}
              max={100}
              className="w-20 rounded-md border border-tribal-border bg-tribal-bg px-3 py-1.5 text-sm text-white focus:border-tribal-red focus:outline-none"
            />
            <span className="text-sm text-tribal-muted">% of total</span>
            <button
              type="button"
              onClick={() => save("deposit_pct", depositPct)}
              disabled={saving === "deposit_pct"}
              className="rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30 disabled:opacity-50"
            >
              {saving === "deposit_pct" ? "…" : "Save"}
            </button>
          </div>
        </section>

        {/* ── Food Package Pricing ──────────────────────────── */}
        <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="text-sm font-semibold text-white mb-1">Food Package Pricing</h3>
          <p className="text-xs text-tribal-muted mb-3">
            Per-person pricing for food packages. These are used as defaults when generating a quote.
          </p>
          <div className="space-y-2">
            {foodPackagePricing.map((fp, i) => (
              <div key={fp.name} className="flex items-center gap-3">
                <span className="text-sm text-white w-36">{fp.name}</span>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-tribal-muted">$</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={fp.price_per_person}
                    onChange={(e) => {
                      setFoodPackagePricing((prev) =>
                        prev.map((item, idx) =>
                          idx === i ? { ...item, price_per_person: Number(e.target.value) } : item
                        )
                      );
                    }}
                    className="w-24 rounded-md border border-tribal-border bg-tribal-bg px-3 py-1.5 text-sm text-white focus:border-tribal-red focus:outline-none"
                  />
                  <span className="text-sm text-tribal-muted">/person</span>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => save("food_package_pricing", foodPackagePricing)}
            disabled={saving === "food_package_pricing"}
            className="mt-3 rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30 disabled:opacity-50"
          >
            {saving === "food_package_pricing" ? "…" : "Save Pricing"}
          </button>
        </section>

        {/* ── Event Email Details ─────────────────────────── */}
        <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="text-sm font-semibold text-white mb-2">Event Email Details</h3>
          <p className="text-sm text-tribal-muted mb-3">
            This content is inserted into the quote email after the "Pay Deposit &amp; Confirm" button.
            Use it for event-specific info like parking, what to wear, waiver links, etc.
          </p>
          <textarea
            value={quoteEmailDetails}
            onChange={(e) => setQuoteEmailDetails(e.target.value)}
            rows={6}
            placeholder="e.g. Please arrive 15 minutes early. Closed-toe shoes required. All participants must sign a waiver…"
            className="w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white resize-none focus:border-tribal-red focus:outline-none placeholder-tribal-muted"
          />
          <p className="mt-1 text-xs text-tribal-muted">
            Leave blank to omit this section from the quote email.
          </p>
          <button
            type="button"
            onClick={() => save("quote_email_details", quoteEmailDetails)}
            disabled={saving === "quote_email_details"}
            className="mt-2 rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30 disabled:opacity-50"
          >
            {saving === "quote_email_details" ? "…" : "Save"}
          </button>
        </section>

        {/* ── Canned Messages / Templates ─────────────────── */}
        <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="text-sm font-semibold text-white mb-1">Email Templates</h3>
          <p className="text-xs text-tribal-muted mb-4">
            Reusable message blocks that can be inserted into emails from the compose window.
            Use these for food package details, event logistics, policies, etc.
          </p>

          <div className="space-y-2">
            {cannedMessages.map((msg, i) => (
              <div key={i} className="flex items-start gap-2 rounded-md border border-tribal-border bg-tribal-bg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-white block">{msg.label}</span>
                  <span className="text-xs text-tribal-muted line-clamp-2 whitespace-pre-wrap">{msg.body}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
                  <button
                    type="button"
                    onClick={() => setEditingCanned({ index: i, label: msg.label, body: msg.body })}
                    className="text-xs text-tribal-muted hover:text-white"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const next = cannedMessages.filter((_, idx) => idx !== i);
                      setCannedMessages(next);
                      save("canned_messages", next);
                    }}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>

          {editingCanned ? (
            <div className="mt-3 rounded-md border border-tribal-red/30 bg-tribal-bg p-3 space-y-2">
              <input
                type="text"
                value={editingCanned.label}
                onChange={(e) => setEditingCanned({ ...editingCanned, label: e.target.value })}
                placeholder="Template name (e.g. Food Packages)"
                className="w-full rounded-md border border-tribal-border bg-tribal-surface px-3 py-1.5 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
              />
              <textarea
                value={editingCanned.body}
                onChange={(e) => setEditingCanned({ ...editingCanned, body: e.target.value })}
                rows={6}
                placeholder="Template content…"
                className="w-full rounded-md border border-tribal-border bg-tribal-surface px-3 py-2 text-sm text-white resize-none placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!editingCanned.label.trim() || !editingCanned.body.trim()) return;
                    const next = [...cannedMessages];
                    if (editingCanned.index >= 0 && editingCanned.index < next.length) {
                      next[editingCanned.index] = { label: editingCanned.label.trim(), body: editingCanned.body.trim() };
                    } else {
                      next.push({ label: editingCanned.label.trim(), body: editingCanned.body.trim() });
                    }
                    setCannedMessages(next);
                    save("canned_messages", next);
                    setEditingCanned(null);
                  }}
                  className="rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30"
                >
                  {editingCanned.index >= 0 && editingCanned.index < cannedMessages.length ? "Update" : "Add"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingCanned(null)}
                  className="rounded px-3 py-1 text-xs text-tribal-muted hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditingCanned({ index: -1, label: "", body: "" })}
              className="mt-3 rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30"
            >
              + Add Template
            </button>
          )}
        </section>

        {/* ── Call Scheduling ──────────────────────────────────── */}
        <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="text-sm font-semibold text-white mb-1">Call Scheduling</h3>
          <p className="text-xs text-tribal-muted mb-4">
            Set when a manager is available for event discussion calls (15-minute slots).
            Scheduled calls themselves are managed on the Scheduled Calls tab.
          </p>

          {/* Weekly Availability */}
          <div className="mb-5 border-b border-tribal-border pb-5">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-tribal-muted mb-3">
              Weekly Availability
            </h4>
            {[
              { day: 0, label: "Sunday" },
              { day: 1, label: "Monday" },
              { day: 2, label: "Tuesday" },
              { day: 3, label: "Wednesday" },
              { day: 4, label: "Thursday" },
              { day: 5, label: "Friday" },
              { day: 6, label: "Saturday" },
            ].map(({ day, label }) => {
              const blocks = availBlocks.filter((b) => b.day_of_week === day);
              return (
                <div key={day} className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white font-medium">{label}</span>
                  </div>
                  {blocks.length === 0 && (
                    <p className="text-xs text-tribal-muted ml-2 italic">No availability</p>
                  )}
                  {blocks.map((block) => (
                    <div key={block.id} className="flex items-center gap-2 ml-2 mb-1">
                      <span className="text-xs text-white">
                        {formatTimeDisplay(block.start_time)} — {formatTimeDisplay(block.end_time)}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          runScheduling(async () => {
                            await deleteAvailabilityBlock(block.id);
                            setAvailBlocks((prev) => prev.filter((b) => b.id !== block.id));
                          }, "Removed")
                        }
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {addingBlock?.day === day ? (
                    <div className="flex items-center gap-2 ml-2 mt-1">
                      <input
                        type="time"
                        value={addingBlock.start}
                        onChange={(e) => setAddingBlock({ ...addingBlock, start: e.target.value })}
                        className="rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-xs text-white focus:border-tribal-red focus:outline-none"
                      />
                      <span className="text-xs text-tribal-muted">to</span>
                      <input
                        type="time"
                        value={addingBlock.end}
                        onChange={(e) => setAddingBlock({ ...addingBlock, end: e.target.value })}
                        className="rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-xs text-white focus:border-tribal-red focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (!addingBlock.start || !addingBlock.end) return;
                          runScheduling(async () => {
                            const res = await addAvailabilityBlock({
                              day_of_week: day,
                              start_time: addingBlock.start,
                              end_time: addingBlock.end,
                            });
                            setAvailBlocks((prev) => [...prev, res.data]);
                            setAddingBlock(null);
                          }, "Added");
                        }}
                        className="text-xs text-green-400 hover:text-green-300"
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddingBlock(null)}
                        className="text-xs text-tribal-muted hover:text-white"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAddingBlock({ day, start: "09:00", end: "12:00" })}
                      className="ml-2 mt-1 text-xs text-tribal-red hover:text-tribal-red/80"
                    >
                      + Add block
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Date Overrides */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-tribal-muted mb-3">
              Date Overrides
            </h4>
            <p className="text-xs text-tribal-muted mb-2">Block out specific dates or add extra hours.</p>
            {dateOverrides.length === 0 && (
              <p className="text-xs text-tribal-muted italic mb-2">No overrides set.</p>
            )}
            {dateOverrides.map((ov) => (
              <div key={ov.id} className="flex items-center gap-2 mb-1">
                <span className="text-xs text-white">
                  {new Date(ov.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  {" — "}
                  {ov.override_type === "unavailable"
                    ? "Unavailable"
                    : `${formatTimeDisplay(ov.start_time!)} – ${formatTimeDisplay(ov.end_time!)}`}
                  {ov.note && <span className="text-tribal-muted"> ({ov.note})</span>}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    runScheduling(async () => {
                      await deleteDateOverride(ov.id);
                      setDateOverrides((prev) => prev.filter((o) => o.id !== ov.id));
                    }, "Removed")
                  }
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  ✕
                </button>
              </div>
            ))}
            {addingOverride ? (
              <div className="mt-2 rounded border border-tribal-border bg-tribal-bg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={addingOverride.date}
                    onChange={(e) => setAddingOverride({ ...addingOverride, date: e.target.value })}
                    className="rounded border border-tribal-border bg-tribal-surface px-2 py-1 text-xs text-white focus:border-tribal-red focus:outline-none"
                  />
                  <select
                    value={addingOverride.type}
                    onChange={(e) => setAddingOverride({ ...addingOverride, type: e.target.value as "unavailable" | "available" })}
                    className="rounded border border-tribal-border bg-tribal-surface px-2 py-1 text-xs text-white focus:border-tribal-red focus:outline-none"
                  >
                    <option value="unavailable">Unavailable (full day)</option>
                    <option value="available">Custom hours</option>
                  </select>
                </div>
                {addingOverride.type === "available" && (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={addingOverride.start}
                      onChange={(e) => setAddingOverride({ ...addingOverride, start: e.target.value })}
                      className="rounded border border-tribal-border bg-tribal-surface px-2 py-1 text-xs text-white focus:border-tribal-red focus:outline-none"
                    />
                    <span className="text-xs text-tribal-muted">to</span>
                    <input
                      type="time"
                      value={addingOverride.end}
                      onChange={(e) => setAddingOverride({ ...addingOverride, end: e.target.value })}
                      className="rounded border border-tribal-border bg-tribal-surface px-2 py-1 text-xs text-white focus:border-tribal-red focus:outline-none"
                    />
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Note (optional)"
                  value={addingOverride.note}
                  onChange={(e) => setAddingOverride({ ...addingOverride, note: e.target.value })}
                  className="w-full rounded border border-tribal-border bg-tribal-surface px-2 py-1 text-xs text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!addingOverride.date) return;
                      runScheduling(async () => {
                        const res = await addDateOverride({
                          date: addingOverride.date,
                          override_type: addingOverride.type,
                          start_time: addingOverride.type === "available" ? addingOverride.start : undefined,
                          end_time: addingOverride.type === "available" ? addingOverride.end : undefined,
                          note: addingOverride.note || undefined,
                        });
                        setDateOverrides((prev) => [...prev, res.data]);
                        setAddingOverride(null);
                      }, "Added");
                    }}
                    className="rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddingOverride(null)}
                    className="rounded px-3 py-1 text-xs text-tribal-muted hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAddingOverride({ date: "", type: "unavailable", start: "09:00", end: "17:00", note: "" })}
                className="mt-2 rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30"
              >
                + Add Date Override
              </button>
            )}
          </div>
        </section>

        {/* ── Widget Embed ─────────────────────────────────── */}
        <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="text-sm font-semibold text-white mb-2">AI Form Widget</h3>
          <p className="text-sm text-tribal-muted mb-2">
            Embed this on your WordPress site to replace the static form.
          </p>
          <code className="block rounded bg-black/40 p-3 text-xs text-green-400 break-all">
            {'<link rel="stylesheet" href="https://events-widget.pages.dev/widget.css" />'}
            <br />
            {'<div id="ta-events-form" data-api-url="https://hq-events-worker.agentengines.workers.dev"></div>'}
            <br />
            {'<script src="https://events-widget.pages.dev/widget.js"></script>'}
          </code>
        </section>
      </div>
    </div>
  );
}
